import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import usePush from '../usePush';

/**
 * The notification switch — on, off, and which kinds.
 *
 * This used to be a button that could only ever turn notifications ON. It
 * destructured `unsubscribe` from the hook and never called it, and had no
 * subscribed state at all, so once you were signed up the button just signed
 * you up again. The only real way out was iOS Settings — which takes the pick
 * reminder with it, the one notification that actually costs you a week, and
 * which the app then can't undo.
 *
 * So: a master switch that genuinely switches off, and a switch per kind
 * underneath it, because "the group chat is too chatty" should not have to mean
 * "never tell me about anything again".
 */

const row = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)'
};

function Switch({ on, onClick, disabled, label }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      aria-label={label}
      style={{
        width: 42, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
        border: '1px solid ' + (on ? 'rgba(76,175,80,0.5)' : 'rgba(255,255,255,0.15)'),
        background: on ? 'rgba(76,175,80,0.30)' : 'rgba(255,255,255,0.07)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        transition: 'all 0.18s', padding: 0
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: on ? 20 : 2,
        width: 18, height: 18, borderRadius: '50%',
        background: on ? '#4caf50' : '#889', transition: 'left 0.18s'
      }} />
    </button>
  );
}

export default function PushNotify() {
  const { state, loading, subscribe, unsubscribe } = usePush();
  const [open, setOpen] = useState(false);
  const [kinds, setKinds] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(null);

  const loadPrefs = useCallback(() => {
    API.get('/push/prefs')
      .then(r => { setKinds(r.data?.kinds || null); setPrefs(r.data?.prefs || null); })
      .catch(() => {});
  }, []);

  useEffect(() => { if (open && !prefs) loadPrefs(); }, [open, prefs, loadPrefs]);

  const setKind = async (kind, enabled) => {
    setSaving(kind);
    // Optimistic: a switch that lags behind the finger feels broken, and the
    // cost of being wrong here is one notification.
    setPrefs(p => ({ ...p, [kind]: enabled }));
    try {
      const r = await API.put('/push/prefs', { kind, enabled });
      if (r.data?.prefs) setPrefs(r.data.prefs);
    } catch (e) {
      setPrefs(p => ({ ...p, [kind]: !enabled }));
    }
    setSaving(null);
  };

  if (state === 'needs_install') {
    return (
      <span
        title="On iPhone, notifications only work once Rivalry Picks is on your Home Screen. Tap Share, then Add to Home Screen, and open it from there."
        style={{ fontSize: 11, color: '#7fb3ff', padding: '6px 10px', cursor: 'help' }}
      >
        🔔 Add to Home Screen for alerts
      </span>
    );
  }

  if (state === 'denied') {
    return (
      <span style={{ fontSize: 11, color: '#445', padding: '6px 10px' }}
            title="Your device is blocking notifications for this app. Only your device settings can allow them again.">
        🔕 Blocked in device settings
      </span>
    );
  }

  // Nothing until we know — otherwise the control flashes "Notifications" at
  // someone who is already subscribed, or at a phone that can't be asked.
  if (state === 'checking' || state === 'unsupported') return null;

  const on = state === 'subscribed';

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => (on ? setOpen(o => !o) : subscribe())}
        disabled={loading}
        title={on ? 'Choose which notifications you get' : 'Get notified: pick reminders, results, pool wins and messages'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
          borderRadius: 8, border: '1px solid ' + (on ? 'rgba(76,175,80,0.35)' : 'rgba(255,255,255,0.12)'),
          background: on ? 'rgba(76,175,80,0.12)' : 'rgba(255,255,255,0.05)',
          color: on ? '#4caf50' : '#889', cursor: 'pointer', fontSize: 12, fontWeight: 700
        }}
      >
        {on ? '🔔' : '🔕'} {loading ? 'Setting up…' : on ? 'Notifications on' : 'Notifications'}
      </button>

      {open && on && (
        <>
          {/* Tap-away layer, so the panel doesn't need a close button on mobile. */}
          <span onClick={() => setOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 400 }} />
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 401,
            width: 280, maxWidth: 'calc(100vw - 32px)', textAlign: 'left',
            background: '#0d1526', border: '1px solid rgba(255,255,255,0.14)',
            borderRadius: 12, padding: '14px 16px', boxShadow: '0 12px 34px rgba(0,0,0,0.6)'
          }}>
            <div style={{ fontFamily: "'Oswald',sans-serif", fontSize: 13, fontWeight: 700, color: '#7fb3ff', letterSpacing: 0.6, marginBottom: 4 }}>
              NOTIFICATIONS
            </div>

            {!prefs ? (
              <div style={{ fontSize: 12, color: '#556', padding: '8px 0' }}>Loading…</div>
            ) : (
              Object.entries(kinds || {}).map(([kind, label]) => (
                <div key={kind} style={row}>
                  <span style={{ fontSize: 12.5, color: '#dde' }}>{label}</span>
                  <Switch on={prefs[kind] !== false} disabled={saving === kind}
                          label={label} onClick={() => setKind(kind, prefs[kind] === false)} />
                </div>
              ))
            )}

            <button
              onClick={async () => { await unsubscribe(); setOpen(false); }}
              disabled={loading}
              style={{
                marginTop: 12, width: '100%', padding: '9px 12px', borderRadius: 8,
                border: '1px solid rgba(213,10,10,0.35)', background: 'rgba(213,10,10,0.12)',
                color: '#ff8888', fontSize: 12, fontWeight: 700, cursor: 'pointer'
              }}
            >
              Turn off notifications on this device
            </button>
            <div style={{ fontSize: 11, color: '#556', marginTop: 8, lineHeight: 1.4 }}>
              Turning them off here can be undone here. Blocking them in your
              phone's settings can't.
            </div>
          </div>
        </>
      )}
    </span>
  );
}
